import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { showToast } from '../config_and_helpers/helpers';
import ManufacturerCache from '../config_and_helpers/manufacturer_cache';
import { logError } from '../config_and_helpers/logger';
import { isCanceledRequest } from '../utils/listSearchRequest';

/**
 * Creates a new manufacturer.
 *
 * Makes a POST request to the backend API to create a new manufacturer. If
 * successful, a success message is displayed to the user. If the request fails,
 * responses are logged and returned.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} manufacturerData - An object containing all the data for the new manufacturer.
 * @returns {Promise<Object>} An object containing key "success" with value true if the API call was successful.
 * If the request fails, returns an array containing messages for each error that occurred.
 * @throws {Error} Will throw an error if the request fails.
 */
export const createManufacturer = async (token, manufacturerData) => {
  try {
    // A POST request is being sent to create the new Manufacturer
    // In the request headers, Authorization is using the supplied token
    await axios.post(`${BACKEND_URL}manufacturers/`, manufacturerData, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });
    // If the request is successful, a success message is displayed to the user
    return {
      success: true,
      toast: () =>
        showToast(
          'Manufacturer created successfully!',
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    // If the request fails, the error responses are logged and returned
    logError('manufacturer_client', 'createManufacturer failed', error.response?.data ?? error);
    return Object.values(error.response?.data ?? {});
  }
};

/**
 * Updates an existing manufacturer.
 *
 * Sends a PATCH request to the backend API to update an already existing manufacturer with the provided
 * updated details. If successful, then it updates the manufacturer state and shows a success
 * message to the user. If the request fails, responses are logged and returned.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} manufacturerId - The ID of the manufacturer to update.
 * @param {Object} updatedManufacturerData - An object containing the updated manufacturer data.
 * @returns {Promise<Object>} An object containing key "success" with value true if the API call was successful.
 * If the request fails, returns an array containing messages for each error that occurred.
 * @throws {Error} Will throw an error if the request fails.
 */
export const updateManufacturer = async (
  token,
  manufacturerId,
  updatedManufacturerData
) => {
  try {
    // A PATCH request is being sent to update the Manufacturer with the specified ID
    // In request headers, the Authorization is using the supplied token
    const response = await axios.patch(
      `${BACKEND_URL}manufacturers/${manufacturerId}/`,
      updatedManufacturerData,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If successful, return an object with a successful status, updated supplier data and a toast function
    return {
      success: true,
      updatedManufacturer: response.data,
      toast: () =>
        showToast(
          'Manufacturer updated successfully!',
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    // If the request fails, the error responses are logged and returned
    logError('manufacturer_client', 'updateManufacturer failed', error.response?.data ?? error);
    return Object.values(error.response?.data ?? {});
  }
};

/**
 * Deletes an existing manufacturer.
 *
 * Sends a DELETE request to the backend API endpoint for the specified manufacturer. If successful,
 * a success message is displayed to the user. If the request fails, responses are logged and returned.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} manufacturerId - The ID of the manufacturer to delete.
 * @returns {Promise<Object>} An object containing key "success" with value true if the API call was successful.
 * If the request fails, returns an array containing messages for each error that occurred.
 * @throws {Error} Will throw an error if the request fails.
 */
export const deleteManufacturer = async (token, manufacturerId) => {
  try {
    // Send a DELETE request to the server to delete the manufacturer with the specified ID
    // Authorization is using the supplied token
    await axios.delete(`${BACKEND_URL}manufacturers/${manufacturerId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // If the request succeeds, a success message is displayed to the user
    return {
      success: true,
      toast: () =>
        showToast(
          'Manufacturer deleted successfully!',
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    // If the request fails, the error responses are logged and returned
    logError('manufacturer_client', 'deleteManufacturer failed', error.response?.data ?? error);
    return Object.values(error.response?.data ?? {});
  }
};

/**
 * Get a list of all manufacturers.
 *
 * Sends a GET request to the backend API to retrieve a list of all manufacturers. The function supports pagination
 * and accepts optional parameters to modify the results such as a search input and a page number for pagination.
 * If successful, it sets the state of the manufacturers in the system and returns an object indicating
 * the success status, the link to the next page of results if they exist, and a flag indicating if the end
 * of the results has been reached.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Function} setManufacturers - The function to update the manufacturers in the component's state.
 * @param {Object} options - An optional object containing searchInput and nextPage parameters.
 * @returns {Promise<Object>} An object with keys "success", "nextPage" (if available), and "reachedEnd". If request fails, returns an array containing messages for each error that occurred.
 * @throws {Error} Will throw an error if the request fails.
 */
export const getManufacturers = async (
  token,
  setManufacturers,
  options = {}
) => {
  const {
    searchInput = '',
    supplierId = '',
    nextPage = null,
    sortBy = '',
    sortDirection = '',
    signal = undefined,
  } = options;

  // Use backend-provided next-page URLs as-is to avoid query drift between pages.
  let url = nextPage ? nextPage : `${BACKEND_URL}manufacturers/?page_num=1`;

  // Only append filters/sort on first-page requests.
  if (!nextPage) {
    if (searchInput) {
      url += `&search=${searchInput}`;
    }
    if (supplierId) {
      url += `&supplier_id=${supplierId}`;
    }
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      url += `&sort_direction=${sortDirection}`;
    }
  }

  try {
    // Send a GET request to fetch the list of manufacturers
    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      signal,
    });

    // Extract 'next' object from response data for pagination
    const nextCursor = response.data.next;

    // If there's no nextCursor available, then either it's a nextPage request or end of manufacturers list
    // is reached in both cases update setManufacturers accordingly
    if (!nextCursor) {
      if (nextPage) {
        setManufacturers(prevManufacturers => [
          ...prevManufacturers,
          ...response.data.results,
        ]);
      } else {
        setManufacturers(response.data.results);
      }
      return { success: true, reachedEnd: true };
    }

    // If it's a nextPage request, then merge old results with new ones
    // Otherwise update all processes with new results
    if (!nextPage) {
      setManufacturers(response.data.results);
    } else {
      setManufacturers(prevManufacturers => [
        ...prevManufacturers,
        ...response.data.results,
      ]);
    }

    // If there are more manufacturers to be displayed in next pages return the
    // success status as true and the url for next page of manufacturers
    return { success: true, nextPage: response.data.next };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    // Log and return any errors that occurred during the request
    logError('manufacturer_client', 'getManufacturers failed', error.response?.data ?? error);
    return Object.values(error.response?.data ?? {});
  }
};

/**
 * Retrieve a list of manufacturer names for selection.
 *
 * Makes a GET request to the backend API to retrieve a list of manufacturer names that can be used for a select list. On successful response, it updates the list of manufacturers in the component's state. In case of failure, responses are logged and returned.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Function} setManufacturers - The function to update the manufacturer list in the component's state.
 * @param {string} supplierId - When creating a product, a supplier ID will be used to filter the associated
 * manufacturers with that supplier
 * @returns {Promise<Object>} An object with key "success" with value true if the API call was successful. If it fails, returns an array containing error messages.
 * @throws {Error} Will throw an error if the request fails.
 */
export const getManufacturerSelectList = async (
  token,
  setManufacturers,
  supplierId
) => {
  // Create the URL and conditionally add the supplier ID query param
  let URL = `${BACKEND_URL}manufacturers/serve_manufacturer_select_list/`;
  if (supplierId) URL += `?supplier_id=${supplierId}`;

  try {
    // Send a GET request to the backend API to fetch the list of manufacturer names
    const response = await axios.get(URL, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // Update the manufacturers' state with the fetched data
    setManufacturers(response.data.manufacturer_list);

    // Return a success indicator
    return { success: true };
  } catch (error) {
    // If the request fails, log the error messages and return them
    logError('manufacturer_client', 'getManufacturerSelectList failed', error.response?.data ?? error);
    if (error.response && error.response.data) {
      return Object.values(error.response.data);
    }
    return { error: 'Failed to fetch manufacturer list' };
  }
};

/**
 * Get the details of a specific manufacturer.
 *
 * Sends a GET request to the backend API to retrieve the details of a specific manufacturer given its ID. If successful, it updates the specified manufacturer details state to reflect the retrieved manufacturer data. If the request fails, responses are logged and returned.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} manufacturerId - The ID of the manufacturer to retrieve details for.
 * @param {Function} setManufacturerDetails - The function to update the manufacturer details in the component state.
 * @returns {Promise<Object>} An object with key "success" with value true if the API call was successful. If it fails, returns an array containing error messages.
 * @throws {Error} Will throw an error if the request fails.
 */
export const getManufacturerDetails = async (
  token,
  manufacturerId,
  setManufacturerDetails
) => {
  try {
    // Send a GET request to the backend API to fetch the details of the specified manufacturer
    const response = await axios.get(
      `${BACKEND_URL}manufacturers/${manufacturerId}/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
    // Update the manufacturer details state with the fetched data
    setManufacturerDetails(response.data);

    // Return a success indicator
    return { success: true };
  } catch (error) {
    // If the request fails, log the error messages and return them
    logError('manufacturer_client', 'getManufacturerDetails failed', error.response?.data ?? error);
    return Object.values(error.response?.data ?? {});
  }
};

/**
 * Get detailed data for a specific manufacturer with products and suppliers.
 * This is the new lazy loading endpoint for detailed manufacturer information.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} manufacturerId - The ID of the manufacturer to retrieve details for.
 * @returns {Promise<Object>} The detailed manufacturer data including products and suppliers.
 * @throws {Error} Will throw an error if the request fails.
 */
export const getManufacturerDetailsLazy = async (token, manufacturerId) => {
  try {
    // Check cache first
    const cached = ManufacturerCache.get(manufacturerId);
    if (cached) {
      return cached;
    }

    // Send a GET request to the get_details endpoint
    const response = await axios.get(
      `${BACKEND_URL}manufacturers/${manufacturerId}/get_details/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Cache the result
    ManufacturerCache.set(manufacturerId, response.data);

    return response.data;
  } catch (error) {
    logError('manufacturer_client', 'getManufacturerDetailsLazy failed', error);
    throw error;
  }
};

/**
 * Get only products for a specific manufacturer.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} manufacturerId - The ID of the manufacturer.
 * @returns {Promise<Array>} Array of products for the manufacturer.
 * @throws {Error} Will throw an error if the request fails.
 */
export const getManufacturerProducts = async (token, manufacturerId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}manufacturers/${manufacturerId}/products/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    logError('manufacturer_client', 'getManufacturerProducts failed', error);
    throw error;
  }
};

/**
 * Get only suppliers for a specific manufacturer.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} manufacturerId - The ID of the manufacturer.
 * @returns {Promise<Array>} Array of suppliers for the manufacturer.
 * @throws {Error} Will throw an error if the request fails.
 */
export const getManufacturerSuppliers = async (token, manufacturerId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}manufacturers/${manufacturerId}/suppliers/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    logError('manufacturer_client', 'getManufacturerSuppliers failed', error);
    throw error;
  }
};

/**
 * Check if a manufacturer name exists.
 *
 * Sends a GET request to the backend API to check whether a manufacturer name already exists. If so, returns an object indicating the name is not unique. If the name doesn't exist or the request fails, responses are logged and returned.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} name - The name of the manufacturer to check.
 * @returns {Promise<Object>} An object with key "unique" and value false if the API call was successful and the manufacturer name exists. If it doesn't exist or the request fails, returns an array containing error messages.
 * @throws {Error} Will throw an error if the request fails.
 */
export const checkManufacturerName = async (token, name) => {
  try {
    // Send a GET request to the backend API to check if the manufacturer name exists
    const response = await axios.get(
      `${BACKEND_URL}manufacturers/check_name/?name=${name}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful, return the uniqueness check result
    return response.data.unique;
  } catch (error) {
    // If the request fails, log the error messages and return them
    logError('manufacturer_client', 'checkManufacturerName failed', error.response?.data ?? error);
    return Object.values(error.response?.data ?? {});
  }
};
